import React, { useRef } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';

const EventTicket = ({ 
  registration, 
  event, 
  showDownload = true,
  size = 'normal' 
}) => {
  const ticketRef = useRef(null);
  const qrRef = useRef(null);

  const ticketCode = registration?.ticket_code || registration?.id?.slice(0, 8).toUpperCase();
  const qrValue = JSON.stringify({
    ticketCode,
    registrationId: registration?.id,
    eventId: event?.id,
    attendeeName: registration?.attendee_name,
    attendeeEmail: registration?.attendee_email,
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const downloadTicket = () => {
    const canvas = document.getElementById(`qr-canvas-${registration?.id}`);
    if (!canvas) return;

    const ticketCanvas = document.createElement('canvas');
    const ctx = ticketCanvas.getContext('2d');
    
    ticketCanvas.width = 400;
    ticketCanvas.height = 600;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 400, 600);

    ctx.fillStyle = '#7c3aed';
    ctx.fillRect(0, 0, 400, 120);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('EVENT TICKET', 200, 50);
    
    ctx.font = '14px Arial';
    ctx.fillText(event?.event || event?.title || 'Conference Event', 200, 80);
    ctx.fillText(ticketCode, 200, 105);

    const qrSize = 180;
    const qrX = (400 - qrSize) / 2;
    ctx.drawImage(canvas, qrX, 140, qrSize, qrSize);

    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 18px Arial';
    ctx.fillText(registration?.attendee_name || 'Attendee', 200, 360);

    ctx.fillStyle = '#6b7280';
    ctx.font = '14px Arial';
    ctx.fillText(registration?.attendee_email || '', 200, 385);

    ctx.fillStyle = '#1f2937';
    ctx.font = '12px Arial';
    ctx.fillText('DATE', 200, 420);
    ctx.font = 'bold 14px Arial';
    ctx.fillText(formatDate(event?.start || event?.date), 200, 440);

    ctx.font = '12px Arial';
    ctx.fillStyle = '#6b7280';
    ctx.fillText('VENUE', 200, 470);
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#1f2937';
    const venue = event?.venue || 'Venue TBD';
    ctx.fillText(venue.length > 35 ? venue.slice(0, 35) + '...' : venue, 200, 490);

    ctx.font = '12px Arial';
    ctx.fillStyle = '#6b7280';
    ctx.fillText('LOCATION', 200, 520);
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#1f2937';
    ctx.fillText(event?.city || 'Location TBD', 200, 540);

    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = '#d1d5db';
    ctx.beginPath();
    ctx.moveTo(20, 560);
    ctx.lineTo(380, 560);
    ctx.stroke();

    ctx.font = '10px Arial';
    ctx.fillStyle = '#9ca3af';
    ctx.fillText('Present this QR code at check-in', 200, 580);

    const link = document.createElement('a');
    link.download = `ticket-${ticketCode}.png`;
    link.href = ticketCanvas.toDataURL('image/png');
    link.click();
  };

  const isCompact = size === 'compact';

  return (
    <div 
      ref={ticketRef}
      className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden ${isCompact ? 'max-w-xs' : 'max-w-md'}`}
    >
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white text-center">
        <p className="text-purple-200 text-xs uppercase tracking-wider mb-1">Event Ticket</p>
        <h3 className={`font-bold ${isCompact ? 'text-lg' : 'text-xl'} leading-tight`}>
          {event?.event || event?.title || 'Conference Event'}
        </h3>
        <p className="text-purple-200 text-sm mt-1 font-mono">{ticketCode}</p>
      </div>

      <div className={`${isCompact ? 'p-4' : 'p-6'} flex flex-col items-center`}>
        <div className="bg-white p-3 rounded-xl shadow-inner border-2 border-gray-100">
          <QRCodeSVG 
            value={qrValue}
            size={isCompact ? 120 : 160}
            level="H"
            includeMargin={false}
          />
        </div>
        
        <QRCodeCanvas 
          id={`qr-canvas-${registration?.id}`}
          value={qrValue}
          size={180}
          level="H"
          style={{ display: 'none' }}
          ref={qrRef}
        />

        <div className="mt-4 text-center">
          <p className={`font-semibold text-gray-900 dark:text-white ${isCompact ? 'text-base' : 'text-lg'}`}>
            {registration?.attendee_name || 'Attendee'}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {registration?.attendee_email}
          </p>
        </div>

        <div className={`w-full ${isCompact ? 'mt-3' : 'mt-4'} border-t border-dashed border-gray-200 dark:border-gray-700 pt-4`}>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</p>
              <p className={`font-medium text-gray-900 dark:text-white ${isCompact ? 'text-xs' : 'text-sm'}`}>
                {formatDate(event?.start || event?.date)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Time</p>
              <p className={`font-medium text-gray-900 dark:text-white ${isCompact ? 'text-xs' : 'text-sm'}`}>
                {formatTime(event?.start || event?.date) || 'TBD'}
              </p>
            </div>
          </div>

          <div className="mt-3 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Venue</p>
            <p className={`font-medium text-gray-900 dark:text-white ${isCompact ? 'text-xs' : 'text-sm'}`}>
              {event?.venue || 'Venue TBD'}
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              {event?.city}
            </p>
          </div>
        </div>

        {registration?.payment_status === 'paid' && (
          <div className="mt-3 inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Paid
          </div>
        )}
      </div>

      {showDownload && (
        <div className="px-6 pb-6">
          <button
            onClick={downloadTicket}
            className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Ticket
          </button>
          
          <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-3">
            Present this QR code at check-in
          </p>
        </div>
      )}
    </div>
  );
};

export default EventTicket;
